
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Bell, User, Sun, Moon, Monitor, LogOut, CheckCircle, ChevronDown, AlertCircle, Calendar, X, Info, Loader2, Palette, Sparkles, Check, Coffee, BookOpen, Clock, Menu } from 'lucide-react';
import { motion } from 'motion/react';
import { View, UserRole, AgendaEntry, ScheduleEntry, Student, AttendanceRecord, AttendanceStatus, Tugas, TugasSubmission, AppNotification, ActivityType, SchoolSettings } from '../types';
import { APP_THEMES, getThemeById, GlobalTheme } from '../src/themeConfig.ts';

interface HeaderProps {
  role: UserRole;
  userName: string;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  activeView: View;
  isDarkMode: boolean;
  themeMode: 'light' | 'dark' | 'system';
  setThemeMode: (mode: 'light' | 'dark' | 'system') => void;
  toggleTheme: () => void;
  onLogout: () => void;
  onOpenProfile: () => void;
  activeUser: any;
  agendas: AgendaEntry[];
  schedules: ScheduleEntry[];
  students: Student[];
  attendanceRecords: AttendanceRecord[];
  tugasList: Tugas[];
  tugasSubmissions: TugasSubmission[];
  notificationsProp?: AppNotification[];
  onNavigate: (view: View, params?: any) => void;
  isBackgroundLoading?: boolean;
  onUpdateUser?: (updated: any) => void;
  onDeleteNotificationData?: (notif: HeaderNotification) => void;
  appTheme?: string;
  onNextTheme?: () => void;
  onSelectTheme?: (themeId: string) => void;
  schoolSettings?: SchoolSettings;
}

interface HeaderNotification {
  id: string;
  title: string;
  message: string;
  type: 'agenda' | 'attendance' | 'info' | 'tugas';
  date: string;
  view?: View;
  params?: any;
}

const Header: React.FC<HeaderProps> = ({ 
  role, 
  userName, 
  setSidebarOpen, 
  activeView, 
  isDarkMode, 
  themeMode,
  setThemeMode,
  toggleTheme, 
  onLogout, 
  onOpenProfile,
  activeUser,
  agendas,
  schedules,
  students,
  attendanceRecords,
  tugasList,
  tugasSubmissions,
  notificationsProp = [],
  onNavigate,
  isBackgroundLoading,
  onUpdateUser,
  onDeleteNotificationData,
  appTheme = 'wzc-themes',
  onNextTheme,
  onSelectTheme,
  schoolSettings
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const [showLiveDetailModal, setShowLiveDetailModal] = useState(false);
  const themeMenuRef = useRef<HTMLDivElement>(null);
  const currentGlobalTheme = useMemo(() => getThemeById(appTheme), [appTheme]);

  // Realtime clock state for live agenda/break countdown
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const activeScheduleItem = useMemo(() => {
    const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    const todayDayName = dayNames[now.getDay()];
    const todayStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;

    const defaultPeriodTimes = [
      { period: 1, startTime: '07:00', endTime: '07:40' },
      { period: 2, startTime: '07:40', endTime: '08:20' },
      { period: 3, startTime: '08:20', endTime: '09:00' },
      { period: 4, startTime: '09:00', endTime: '09:40' },
      { period: 5, startTime: '10:00', endTime: '10:40' },
      { period: 6, startTime: '10:40', endTime: '11:20' },
      { period: 7, startTime: '11:20', endTime: '12:00' },
      { period: 8, startTime: '12:30', endTime: '13:10' },
      { period: 9, startTime: '13:10', endTime: '13:50' },
      { period: 10, startTime: '13:50', endTime: '14:30' },
    ];

    const periodsToUse = (schoolSettings?.dayPeriodTimes?.[todayDayName] && schoolSettings.dayPeriodTimes[todayDayName].length > 0)
      ? schoolSettings.dayPeriodTimes[todayDayName]
      : (schoolSettings?.periodTimes && schoolSettings.periodTimes.length > 0 ? schoolSettings.periodTimes : defaultPeriodTimes);

    const sortedPeriods = [...periodsToUse].sort((a, b) => a.period - b.period);

    const timeToSecs = (str: string) => {
      if (!str) return 0;
      const clean = str.trim().replace('.', ':');
      const [h, m] = clean.split(':').map(Number);
      return (h || 0) * 3600 + (m || 0) * 60;
    };

    const currentSecs = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

    const formatRemaining = (secs: number) => {
      if (secs <= 0) return '00m 00s';
      const hrs = Math.floor(secs / 3600);
      const mins = Math.floor((secs % 3600) / 60);
      const s = secs % 60;
      if (hrs > 0) {
        return `${hrs}j ${mins}m`;
      }
      return `${mins.toString().padStart(2, '0')}m ${s.toString().padStart(2, '0')}s`;
    };

    const teacherSubject = (activeUser?.subject || '').trim().toLowerCase();
    const assignedClassesNormalized = (activeUser?.assignedClasses || []).map((c: string) => 
      (c || '').trim().toLowerCase().replace(/^kelas\s+/i, '')
    );

    // Filter guru's schedules & agendas for today
    const todayTeacherSchedules = (schedules || []).filter(s => {
      if (s.day !== todayDayName) return false;
      if (s.isBreak || s.subject === 'Jam Istirahat' || s.class === 'Istirahat') return true;
      const sClass = (s.class || '').trim().toLowerCase().replace(/^kelas\s+/i, '');
      const sSubject = (s.subject || '').trim().toLowerCase();
      const isClassMatch = assignedClassesNormalized.length > 0 && assignedClassesNormalized.includes(sClass);
      const isSubjectMatch = Boolean(teacherSubject && sSubject && sSubject === teacherSubject);
      return isClassMatch || isSubjectMatch;
    });

    const todayTeacherAgendas = (agendas || []).filter(a => {
      if (a.date !== todayStr || a.isArchived) return false;
      if (a.teacherId && activeUser?.id && a.teacherId === activeUser.id) return true;
      if (a.teacherName && activeUser?.name && a.teacherName.trim().toLowerCase() === activeUser.name.trim().toLowerCase()) return true;
      const aClass = (a.class || '').trim().toLowerCase().replace(/^kelas\s+/i, '');
      const aSubject = (a.subject || '').trim().toLowerCase();
      const isClassMatch = assignedClassesNormalized.length > 0 && assignedClassesNormalized.includes(aClass);
      const isSubjectMatch = Boolean(teacherSubject && aSubject && aSubject === teacherSubject);
      return isClassMatch || isSubjectMatch;
    });

    // 1. Check if we have an active period right now
    if (sortedPeriods.length > 0) {
      for (let i = 0; i < sortedPeriods.length; i++) {
        const pt = sortedPeriods[i];
        const startSecs = timeToSecs(pt.startTime);
        const endSecs = timeToSecs(pt.endTime);

        if (currentSecs >= startSecs && currentSecs < endSecs) {
          const remainingSecs = endSecs - currentSecs;

          const scheduleEntry = todayTeacherSchedules.find(s => 
            Math.abs(s.period - pt.period) < 0.1
          );

          const activeAgenda = todayTeacherAgendas.find(a => 
            pt.period >= (a.startPeriod || 1) && 
            pt.period <= (a.endPeriod || a.startPeriod || 1)
          );

          const isBreak = scheduleEntry?.isBreak || 
            scheduleEntry?.subject === 'Jam Istirahat' || 
            scheduleEntry?.class === 'Istirahat' || 
            activeAgenda?.subject === 'Jam Istirahat' || 
            activeAgenda?.class === 'Istirahat';

          if (isBreak) {
            return {
              title: 'Jam Istirahat',
              shortClass: 'Istirahat',
              subTitle: `Istirahat`,
              timeRange: `${pt.startTime} - ${pt.endTime}`,
              startTime: pt.startTime,
              endTime: pt.endTime,
              remainingText: formatRemaining(remainingSecs),
              remainingSecs,
              type: 'break' as const,
              periodNumber: pt.period,
              subject: 'Jam Istirahat',
              topic: 'Istirahat',
              tpCode: '',
              tpDescription: '',
              teacherName: activeUser?.name || '',
              agendaStatus: 'Istirahat',
            };
          }

          const rawClass = (activeAgenda?.class || scheduleEntry?.class)?.trim() || '';
          const hasValidClass = rawClass && rawClass !== '-' && rawClass.toLowerCase() !== 'istirahat' && rawClass.toLowerCase() !== 'none';

          if (hasValidClass) {
            const classLabel = rawClass.toLowerCase().startsWith('kelas') ? rawClass : `Kelas ${rawClass}`;
            const cleanShortClass = rawClass.replace(/^kelas\s+/i, '');
            return {
              title: classLabel,
              shortClass: cleanShortClass,
              subTitle: `Jam ke-${pt.period}`,
              timeRange: `${pt.startTime} - ${pt.endTime}`,
              startTime: pt.startTime,
              endTime: pt.endTime,
              remainingText: formatRemaining(remainingSecs),
              remainingSecs,
              type: 'class' as const,
              periodNumber: pt.period,
              subject: activeAgenda?.subject || scheduleEntry?.subject || activeUser?.subject || '',
              topic: activeAgenda?.topic || activeAgenda?.tpDescription || scheduleEntry?.subject || 'Kegiatan Pembelajaran',
              tpCode: activeAgenda?.tpCode || '',
              tpDescription: activeAgenda?.tpDescription || '',
              teacherName: activeAgenda?.teacherName || activeUser?.name || '',
              agendaStatus: activeAgenda?.status || 'Sedang Berlangsung',
              agendaId: activeAgenda?.id
            };
          }

          const agendaTopic = activeAgenda?.topic?.trim() || 
            activeAgenda?.tpDescription?.trim() || 
            activeAgenda?.notes?.trim();

          if (agendaTopic) {
            return {
              title: agendaTopic,
              shortClass: activeAgenda?.class ? activeAgenda.class.replace(/^kelas\s+/i, '') : 'Agenda',
              subTitle: `Jam ke-${pt.period}`,
              timeRange: `${pt.startTime} - ${pt.endTime}`,
              startTime: pt.startTime,
              endTime: pt.endTime,
              remainingText: formatRemaining(remainingSecs),
              remainingSecs,
              type: 'class' as const,
              periodNumber: pt.period,
              subject: activeAgenda?.subject || activeUser?.subject || '',
              topic: agendaTopic,
              tpCode: activeAgenda?.tpCode || '',
              tpDescription: activeAgenda?.tpDescription || '',
              teacherName: activeAgenda?.teacherName || activeUser?.name || '',
              agendaStatus: activeAgenda?.status || 'Sedang Berlangsung',
              agendaId: activeAgenda?.id
            };
          }

          // Jika pada jam ini guru tidak memiliki jadwal/agenda ataupun istirahat, jangan tampilkan kartu
          return null;
        }

        // Cek jika sedang berada di sela-sela jam istirahat antar periode
        if (i < sortedPeriods.length - 1) {
          const nextPt = sortedPeriods[i + 1];
          const nextStartSecs = timeToSecs(nextPt.startTime);
          if (currentSecs >= endSecs && currentSecs < nextStartSecs) {
            const remainingSecs = nextStartSecs - currentSecs;
            return {
              title: 'Jam Istirahat',
              shortClass: 'Istirahat',
              subTitle: `Sebelum Jam ${nextPt.period}`,
              timeRange: `${pt.endTime} - ${nextPt.startTime}`,
              startTime: pt.endTime,
              endTime: nextPt.startTime,
              remainingText: formatRemaining(remainingSecs),
              remainingSecs,
              type: 'break' as const,
              periodNumber: nextPt.period,
              subject: 'Jam Istirahat',
              topic: 'Istirahat',
              tpCode: '',
              tpDescription: '',
              teacherName: activeUser?.name || '',
              agendaStatus: 'Istirahat',
            };
          }
        }
      }
    }

    // Jika di luar jam pelajaran, akhir pekan, atau tidak ada agenda/istirahat aktif, tidak perlu menampilkan kartu
    return null;
  }, [now, schedules, agendas, activeUser, schoolSettings]);
  const [dismissedNotifs, setDismissedNotifs] = useState<string[]>(() => {
    const saved = localStorage.getItem('smpn1_dismissed_notifications');
    const local = saved ? JSON.parse(saved) : [];
    return activeUser?.dismissedNotifications || local;
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // Sync state if activeUser changes (e.g. sync from other device)
  useEffect(() => {
    if (activeUser?.dismissedNotifications) {
      setDismissedNotifs(activeUser.dismissedNotifications);
    }
  }, [activeUser?.dismissedNotifications]);

  const handleDismissNotif = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const updated = Array.from(new Set([...dismissedNotifs, id]));
    setDismissedNotifs(updated);
    localStorage.setItem('smpn1_dismissed_notifications', JSON.stringify(updated));
    
    if (onUpdateUser && activeUser) {
      onUpdateUser({ ...activeUser, dismissedNotifications: updated });
    }

    // Direct deletion from database if requested
    if (onDeleteNotificationData) {
      const notif = notifications.find(n => n.id === id);
      if (notif) onDeleteNotificationData(notif);
    }
  };

  const handleDismissAll = (notifs: HeaderNotification[]) => {
    const ids = notifs.map(n => n.id);
    const updated = Array.from(new Set([...dismissedNotifs, ...ids]));
    setDismissedNotifs(updated);
    localStorage.setItem('smpn1_dismissed_notifications', JSON.stringify(updated));
    
    if (onUpdateUser && activeUser) {
      onUpdateUser({ ...activeUser, dismissedNotifications: updated });
    }

    // Direct deletion from database if requested
    if (onDeleteNotificationData) {
      notifs.forEach(n => onDeleteNotificationData(n));
    }
  };

  // Calculate Notifications
  const notifications = useMemo(() => {
    const list: HeaderNotification[] = [];
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const dayNames: string[] = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

    // 0. Persistent DB Notifications (e.g. Student submissions)
    if (activeUser && Array.isArray(notificationsProp)) {
      notificationsProp.forEach(n => {
        if (n && n.userId === activeUser.id && !dismissedNotifs.includes(n.id)) {
          list.push({
            id: n.id,
            title: n.title,
            message: n.message,
            type: 'tugas', 
            date: n.timestamp,
            view: n.relatedId ? View.Tugas : undefined,
            params: n.relatedId ? { tugasId: n.relatedId } : undefined
          });
        }
      });
    }

    if (role === UserRole.Guru && activeUser) {
      const teacherClasses = (activeUser.assignedClasses || []).map((c: string) => c.trim().toLowerCase());
      const teacherSubject = (activeUser.subject || '').trim().toLowerCase();

      // 1. Agenda Belum Selesai (Statistik atau Absensi Belum)
      agendas.forEach(a => {
        const agSubject = (a.subject || '').trim().toLowerCase();
        const agClass = (a.class || '').trim().toLowerCase();
        
        if (agSubject !== teacherSubject) return;
        if (!teacherClasses.includes(agClass)) return;

        // Skip extra agendas/SAS
        const isExtraAgendaWithoutClass = (a.activityType === ActivityType.SUMATIF || a.activityType === ActivityType.SAS || (a.activityType as any) === 'SAS' || (a.activityType as any) === 'Sumatif Akhir') && (!a.class || a.class === '');
        const isJamKosong = a.status === 'Jam kosong';
        const isNoAbsenceActivity = a.class ? !!a.isAttendanceOptional : ([
          ActivityType.KEGIATAN_LOMBA, 
          ActivityType.KEGIATAN_KESISWAAN, 
          ActivityType.KEGIATAN_SEKOLAH, 
          ActivityType.SAS,
          ActivityType.PERWALIAN
        ].includes(a.activityType as any) || 
        (a.activityType as any) === 'SAS' || 
        (a.activityType as any) === 'Sumatif Akhir' ||
        a.isAttendanceOptional);
        
        if (isExtraAgendaWithoutClass || isJamKosong || isNoAbsenceActivity) return;

        // Check Attendance Completeness
        const classStudents = students.filter(s => (s.class || '').trim().toLowerCase() === agClass && s.status === 'Aktif');
        const recordCount = attendanceRecords.filter(r => r.agendaId === a.id).length;
        
        // Show only if attendance has started (recordCount > 0) but is not complete
        if (recordCount > 0 && recordCount < classStudents.length && classStudents.length > 0) {
          const notifId = `notif-incomplete-att-${a.id}`;
          if (!dismissedNotifs.includes(notifId)) {
            list.push({
              id: notifId,
              title: 'Tanggungan Absensi',
              message: `Absensi kelas ${a.class} tanggal ${a.date} belum lengkap (${recordCount}/${classStudents.length} siswa).`,
              type: 'attendance',
              date: a.date,
              view: View.Attendance
            });
          }
        }

        // Agenda Terlambat (Status Belum Selesai > 1 Hari)
        if (a.status !== 'Selesai') {
          const agendaDate = new Date(a.date);
          if (agendaDate < oneDayAgo) {
            const notifId = `notif-overdue-agenda-${a.id}`;
            if (!dismissedNotifs.includes(notifId)) {
              list.push({
                id: notifId,
                title: 'Agenda Terlambat',
                message: `Agenda kelas ${a.class} tanggal ${a.date} belum diselesaikan (Status: ${a.status}).`,
                type: 'agenda',
                date: a.date,
                view: View.Agenda
              });
            }
          }
        }
      });

      // 3. Siswa > 3 Kali Alfa
      const studentAbsences: Record<string, number> = {};
      attendanceRecords.forEach(r => {
        if (r.status === AttendanceStatus.Alpa) {
          studentAbsences[r.studentId] = (studentAbsences[r.studentId] || 0) + 1;
        }
      });

      Object.entries(studentAbsences).forEach(([studentId, count]) => {
        const notifId = `notif-att-alfa-${studentId}`;
        if (count > 3 && !dismissedNotifs.includes(notifId)) {
          const student = students.find(s => s.id === studentId);
          if (student && teacherClasses.includes((student.class || '').trim().toLowerCase())) {
            list.push({
              id: notifId,
              title: 'Peringatan Alfa',
              message: `Siswa ${student.name} (${student.class}) sudah ${count} kali Alpa.`,
              type: 'attendance',
              date: today,
              view: View.Attendance
            });
          }
        }
      });
    }

    return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [agendas, schedules, students, attendanceRecords, tugasList, tugasSubmissions, notificationsProp, role, activeUser, dismissedNotifs]);

  // Menutup menu jika mengklik di luar area profil atau notif atau theme menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setIsNotifOpen(false);
      }
      if (themeMenuRef.current && !themeMenuRef.current.contains(event.target as Node)) {
        setIsThemeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className={`h-16 flex items-center justify-between px-2.5 sm:px-6 md:px-8 border-b ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'} sticky top-0 z-30 app-header gap-1.5 sm:gap-2 min-w-0`}>
      {/* Left side: Navigation / Page Title */}
      <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 shrink">
        {setSidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 sm:p-2 -ml-1 sm:hidden text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors shrink-0 active:scale-95"
            title="Buka Menu Navigasi"
            aria-label="Buka Menu Navigasi"
          >
            <Menu size={19} />
          </button>
        )}
        <div className="flex flex-col min-w-0">
          <h2 className="text-xs xs:text-sm sm:text-base md:text-lg font-black text-slate-800 dark:text-white leading-tight truncate max-w-[70px] xs:max-w-[100px] sm:max-w-[180px] md:max-w-none">
            {activeView}
          </h2>
          <p className="text-[9px] sm:text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden sm:block truncate">
            SMPN 1 Kawali
          </p>
        </div>
      </div>

      {/* Active Agenda / Break Card for Teachers & Admin */}
      {(role === UserRole.Guru || role === UserRole.Admin || activeUser?.role === UserRole.Guru || (activeUser?.assignedClasses && activeUser.assignedClasses.length > 0)) && activeScheduleItem && (
        <div 
          onClick={() => setShowLiveDetailModal(true)}
          title="Klik untuk melihat detail jadwal & agenda live"
          className="cursor-pointer group select-none flex-1 max-w-[190px] xs:max-w-[240px] sm:max-w-[320px] md:max-w-none flex justify-center min-w-0 mx-0.5"
        >
          {/* Desktop & Tablet Full Card (>= md) */}
          <div className={`hidden md:flex items-center gap-2.5 px-3 py-1.5 rounded-2xl border text-xs shadow-2xs transition-all shrink-0 group-hover:scale-[1.02] group-active:scale-[0.98] ${
            activeScheduleItem.type === 'class'
              ? 'bg-indigo-50/90 dark:bg-indigo-950/60 border-indigo-200/90 dark:border-indigo-800/80 text-indigo-900 dark:text-indigo-200 hover:border-indigo-400'
              : 'bg-amber-50/90 dark:bg-amber-950/60 border-amber-200/90 dark:border-amber-800/80 text-amber-900 dark:text-amber-200 hover:border-amber-400'
          }`}>
            <div className="relative flex items-center justify-center shrink-0">
              {activeScheduleItem.type === 'break' ? (
                <div className="p-1.5 bg-amber-500 text-white rounded-xl shadow-2xs">
                  <Coffee size={13} />
                </div>
              ) : (
                <div className="p-1.5 bg-indigo-600 text-white rounded-xl shadow-2xs">
                  <BookOpen size={13} />
                </div>
              )}
              <span className="absolute -top-1 -right-1 flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  activeScheduleItem.type === 'break' ? 'bg-amber-400' : 'bg-emerald-400'
                }`}></span>
                <span className={`relative inline-flex rounded-full h-2 w-2 ${
                  activeScheduleItem.type === 'break' ? 'bg-amber-500' : 'bg-emerald-500'
                }`}></span>
              </span>
            </div>

            <div className="flex flex-col leading-tight">
              <div className="flex items-center gap-1.5">
                <span 
                  className="font-extrabold text-xs tracking-tight truncate max-w-[140px] lg:max-w-[240px]"
                  title={activeScheduleItem.title}
                >
                  {activeScheduleItem.title}
                </span>
                {activeScheduleItem.subTitle && (
                  <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-md shrink-0 ${
                    activeScheduleItem.type === 'class'
                      ? 'bg-indigo-200/80 dark:bg-indigo-900/80 text-indigo-800 dark:text-indigo-300'
                      : 'bg-amber-200/80 dark:bg-amber-900/80 text-amber-800 dark:text-amber-300'
                  }`}>
                    {activeScheduleItem.subTitle}
                  </span>
                )}
              </div>
              {activeScheduleItem.remainingText && (
                <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                  <span>Sisa:</span>
                  <span className={`font-mono font-black ${
                    activeScheduleItem.type === 'class'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`}>
                    {activeScheduleItem.remainingText}
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Compact Responsive Mobile Pill (< md) */}
          <div className={`flex md:hidden items-center gap-1.5 px-2 py-1 rounded-xl border shadow-2xs transition-all w-full min-w-0 ${
            activeScheduleItem.type === 'class'
              ? 'bg-indigo-50/95 dark:bg-indigo-950/80 border-indigo-200 dark:border-indigo-800/90 text-indigo-950 dark:text-indigo-100 hover:border-indigo-400'
              : 'bg-amber-50/95 dark:bg-amber-950/80 border-amber-200 dark:border-amber-800/90 text-amber-950 dark:text-amber-100 hover:border-amber-400'
          }`}>
            <div className="relative flex items-center justify-center shrink-0">
              {activeScheduleItem.type === 'break' ? (
                <div className="p-1 bg-amber-500 text-white rounded-lg">
                  <Coffee size={10} />
                </div>
              ) : (
                <div className="p-1 bg-indigo-600 text-white rounded-lg">
                  <BookOpen size={10} />
                </div>
              )}
              <span className="absolute -top-0.5 -right-0.5 flex h-1.5 w-1.5">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  activeScheduleItem.type === 'break' ? 'bg-amber-400' : 'bg-emerald-400'
                }`}></span>
                <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                  activeScheduleItem.type === 'break' ? 'bg-amber-500' : 'bg-emerald-500'
                }`}></span>
              </span>
            </div>

            <div className="flex flex-col min-w-0 flex-1 leading-none justify-center">
              <div className="flex items-center gap-1 min-w-0">
                <span className="font-black text-[10px] xs:text-[11px] truncate tracking-tight text-slate-900 dark:text-white">
                  {activeScheduleItem.type === 'break' ? 'Istirahat' : (activeScheduleItem.title || `Kelas ${activeScheduleItem.shortClass}`)}
                </span>
                {activeScheduleItem.periodNumber ? (
                  <span className={`text-[8px] font-black uppercase px-1 py-0.5 rounded shrink-0 ${
                    activeScheduleItem.type === 'break'
                      ? 'bg-amber-200 dark:bg-amber-900/80 text-amber-800 dark:text-amber-200'
                      : 'bg-indigo-200 dark:bg-indigo-900/80 text-indigo-800 dark:text-indigo-200'
                  }`}>
                    J-{activeScheduleItem.periodNumber}
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-1 text-[9px] text-slate-500 dark:text-slate-400 font-bold mt-0.5">
                <span className="text-[8px] opacity-75">⏱</span>
                <span className={`font-mono font-black text-[9px] ${
                  activeScheduleItem.type === 'class'
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`}>
                  {activeScheduleItem.remainingText}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Right side: Actions (Sync, Themes, Dark Mode, Notifications, User) */}
      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        {isBackgroundLoading && (
          <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full border border-indigo-100 dark:border-indigo-800">
            <Loader2 size={12} className="animate-spin shrink-0" />
            <span className="text-[10px] font-medium hidden sm:inline">Syncing...</span>
          </div>
        )}

        {/* Global App Themes Switcher */}
        <div className="relative" ref={themeMenuRef}>
          {/* Mobile Theme Button (< sm) */}
          <div className="flex sm:hidden items-center">
            <button
              onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
              className="p-1.5 bg-slate-100 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700/60 flex items-center justify-center hover:bg-white dark:hover:bg-slate-700 transition-colors"
              title={`Ganti Tema (${currentGlobalTheme.name})`}
            >
              <div 
                className="w-3.5 h-3.5 rounded-full shadow-xs shrink-0 border border-white/20"
                style={{ backgroundColor: currentGlobalTheme.primaryColor }}
              />
            </button>
          </div>

          {/* Desktop/Tablet Theme Switcher (>= sm) */}
          <div className="hidden sm:flex items-center bg-slate-100 dark:bg-slate-800/80 rounded-xl p-0.5 border border-slate-200 dark:border-slate-700/60 shadow-xs">
            <button
              onClick={onNextTheme}
              className="px-2 py-1.5 hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-all flex items-center gap-1.5 text-slate-700 dark:text-slate-200 group cursor-pointer"
              title={`Ganti tema berikutnya (Sekarang: ${currentGlobalTheme.name})`}
            >
              <div 
                className="w-3.5 h-3.5 rounded-full shadow-xs shrink-0 border border-white/20"
                style={{ backgroundColor: currentGlobalTheme.primaryColor }}
              />
              <span className="text-[10px] sm:text-xs font-black tracking-tight whitespace-nowrap max-w-[80px] md:max-w-[120px] truncate">
                {currentGlobalTheme.shortName}
              </span>
              <span className="hidden md:inline-block text-[9px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/50 px-1.5 py-0.5 rounded">
                Next
              </span>
            </button>
            <button
              onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
              className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors"
              title="Pilih Tema Aplikasi"
            >
              <Palette size={14} />
            </button>
          </div>

          {/* Theme Dropdown */}
          {isThemeMenuOpen && (
            <>
              {/* Backdrop on mobile */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-40 sm:hidden" 
                onClick={() => setIsThemeMenuOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="fixed inset-x-3 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-full mt-2 w-auto sm:w-80 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-2.5 z-50 max-h-[80vh] flex flex-col origin-top-right"
              >
                <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 mb-1 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-2">
                    <Palette size={15} className="text-indigo-600 dark:text-indigo-400" />
                    <span className="text-xs font-black text-slate-800 dark:text-white">Pilih Tema Aplikasi</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-300 px-2 py-0.5 rounded-full">
                      {APP_THEMES.length} Pilihan
                    </span>
                    <button 
                      onClick={() => setIsThemeMenuOpen(false)}
                      className="sm:hidden p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                <div className="space-y-1 overflow-y-auto custom-scrollbar flex-1 pr-1 max-h-[60vh]">
                  {APP_THEMES.map((th) => {
                    const isSelected = th.id === appTheme;
                    return (
                      <button
                        key={th.id}
                        onClick={() => {
                          if (onSelectTheme) onSelectTheme(th.id);
                          setIsThemeMenuOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between group cursor-pointer ${
                          isSelected 
                            ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800' 
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span 
                            className="w-3.5 h-3.5 rounded-full shrink-0 shadow-2xs border border-white/20"
                            style={{ backgroundColor: th.primaryColor }}
                          />
                          <div className="truncate">
                            <div className="font-black truncate flex items-center gap-1.5">
                              <span>{th.name}</span>
                            </div>
                            <div className="text-[9px] text-slate-400 dark:text-slate-500 font-normal truncate max-w-[200px]">{th.description}</div>
                          </div>
                        </div>
                        {isSelected && <Check size={14} className="text-indigo-600 dark:text-indigo-400 shrink-0 ml-2" />}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </>
          )}
        </div>

        {/* Dark / Light / System Mode Toggle */}
        <div className="relative group">
          <button 
            onClick={toggleTheme}
            className="p-1.5 sm:p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors flex items-center gap-1.5"
            title={`Mode: ${themeMode === 'system' ? 'Sesuai Sistem' : themeMode === 'light' ? 'Terang' : 'Gelap'}`}
          >
            {themeMode === 'system' ? (
              <Monitor size={18} className="text-indigo-500" />
            ) : (
              isDarkMode ? <Moon size={18} className="text-amber-400" /> : <Sun size={18} className="text-orange-500" />
            )}
            <div className="hidden lg:flex flex-col items-start leading-none gap-0.5">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 group-hover:text-indigo-500 transition-colors">
                {themeMode === 'system' ? 'Sistem' : themeMode === 'light' ? 'Terang' : 'Gelap'}
              </span>
              <span className="text-[8px] font-medium text-slate-500 dark:text-slate-400">
                Mode
              </span>
            </div>
          </button>
        </div>

        {/* Notifications Popover */}
        <div className="relative" ref={notifRef}>
          <button 
            onClick={() => setIsNotifOpen(!isNotifOpen)}
            className="p-1.5 sm:p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors relative"
            title="Notifikasi"
          >
            <Bell size={18} />
            {notifications.length > 0 && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full border-2 border-white dark:border-slate-900"></span>
            )}
          </button>

          {isNotifOpen && (
            <>
              {/* Backdrop for mobile */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-40 sm:hidden" 
                onClick={() => setIsNotifOpen(false)}
              />
              <motion.div 
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="fixed inset-x-3 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-full mt-2 sm:w-96 bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2rem] shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50 origin-top-right"
              >
                <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
                  <div>
                    <h3 className="text-xs sm:text-sm font-black text-slate-800 dark:text-white uppercase tracking-wider leading-none">Notifikasi</h3>
                    <p className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Info & Pembaruan</p>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-[10px] bg-indigo-600 text-white px-2.5 py-0.5 rounded-full font-black shadow-lg shadow-indigo-500/20">
                      {notifications.length}
                    </span>
                    <button 
                      onClick={() => setIsNotifOpen(false)}
                      className="sm:hidden p-1.5 bg-slate-100 dark:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                {notifications.length > 0 && (
                  <div className="px-4 sm:px-6 py-2.5 bg-indigo-50/30 dark:bg-indigo-900/10 border-b border-slate-100 dark:border-slate-700 flex justify-end">
                    <button 
                      onClick={() => handleDismissAll(notifications)}
                      className="text-[9px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest hover:underline flex items-center gap-1.5"
                    >
                      <CheckCircle size={12} /> Tandai Semua Selesai
                    </button>
                  </div>
                )}
                <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto custom-scrollbar">
                  {notifications.length > 0 ? (
                      notifications.map(notif => (
                        <div key={notif.id} className="relative group/item border-b border-slate-50 dark:border-slate-700/50 last:border-0 transition-colors">
                          <button 
                            onClick={(e) => {
                              if (notif.type === 'tugas') {
                                handleDismissNotif(e, notif.id);
                              }
                              if (notif.view) onNavigate(notif.view, notif.params);
                              setIsNotifOpen(false);
                            }}
                            className="w-full text-left px-4 sm:px-6 py-4 sm:py-5 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-all flex gap-3 sm:gap-4"
                          >
                            <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0 shadow-xs transition-transform group-hover/item:scale-105 ${
                              notif.type === 'agenda' ? 'bg-amber-100 text-amber-600 border border-amber-200' : 
                              notif.type === 'attendance' ? 'bg-rose-100 text-rose-600 border border-rose-200' : 
                              notif.type === 'tugas' ? 'bg-emerald-100 text-emerald-600 border border-emerald-200' : 'bg-blue-100 text-blue-600 border border-blue-200'
                            }`}>
                              {notif.type === 'agenda' ? <Calendar size={18} /> : 
                               notif.type === 'attendance' ? <AlertCircle size={18} /> : 
                               notif.type === 'tugas' ? <CheckCircle size={18} /> : <Info size={18} />}
                            </div>
                            <div className="flex-1 min-w-0 pr-6">
                              <p className="text-xs font-black text-slate-800 dark:text-white mb-0.5 uppercase tracking-tight truncate">{notif.title}</p>
                              <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed line-clamp-2 mb-1">{notif.message}</p>
                              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">{new Date(notif.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}</p>
                            </div>
                          </button>
                          <button 
                            onClick={(e) => handleDismissNotif(e, notif.id)}
                            className="absolute right-3 sm:right-4 top-4 sm:top-5 p-1.5 text-slate-300 hover:text-rose-500 opacity-100 sm:opacity-0 group-hover/item:opacity-100 transition-all rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20"
                            title="Hapus Notifikasi"
                          >
                            <X size={15} />
                          </button>
                        </div>
                      ))
                  ) : (
                    <div className="p-10 sm:p-16 text-center">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-50 dark:bg-slate-900 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-3 sm:mb-4 border border-slate-100 dark:border-slate-800">
                        <CheckCircle className="text-slate-200 dark:text-slate-700" size={32} />
                      </div>
                      <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Semua Beres!</p>
                      <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-tighter">Tidak ada notifikasi baru</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </div>

        {/* Profile / User Account Dropdown */}
        <div className="relative" ref={menuRef}>
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`flex items-center gap-1.5 sm:gap-2.5 p-1 sm:p-1.5 rounded-2xl transition-all border ${isMenuOpen ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800' : 'hover:bg-slate-100 dark:hover:bg-slate-800 border-transparent'}`}
          >
            <div className={`w-8 h-8 sm:w-9 sm:h-9 bg-indigo-600 rounded-xl flex items-center justify-center text-white text-xs sm:text-sm font-black shadow-lg shadow-indigo-500/20 transition-transform shrink-0 ${isMenuOpen ? 'scale-90' : ''}`}>
              {userName ? userName[0] : 'U'}
            </div>
            <div className="hidden md:block text-left mr-1">
              <p className="text-xs font-black text-slate-800 dark:text-white leading-none mb-1 uppercase tracking-tight truncate max-w-[120px]">{userName}</p>
              <p className="text-[9px] text-indigo-500 dark:text-indigo-400 font-bold uppercase tracking-widest leading-none">{role}</p>
            </div>
            <ChevronDown size={14} className={`text-slate-400 transition-transform duration-300 hidden sm:block ${isMenuOpen ? 'rotate-180 text-indigo-500' : ''}`} />
          </button>
          
          {isMenuOpen && (
            <>
              {/* Backdrop for mobile */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-40 sm:hidden" 
                onClick={() => setIsMenuOpen(false)}
              />
              <motion.div 
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="fixed inset-x-3 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-full mt-2 w-auto sm:w-56 bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[1.5rem] shadow-2xl border border-slate-200 dark:border-slate-700 p-2 z-50 origin-top-right"
              >
                <div className="px-3 py-3 mb-2 border-b border-slate-100 dark:border-slate-700 md:hidden flex items-center justify-between">
                  <div className="min-w-0 pr-2">
                    <p className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-tight truncate">{userName}</p>
                    <p className="text-[9px] text-indigo-500 dark:text-indigo-400 font-black uppercase tracking-widest">{role}</p>
                  </div>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                </div>
                <button 
                  onClick={() => { setIsMenuOpen(false); onOpenProfile(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-xl transition-all group cursor-pointer"
                >
                  <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/40 group-hover:text-indigo-600 transition-colors">
                    <User size={14} />
                  </div>
                  Profil Saya
                </button>
                <div className="h-px bg-slate-100 dark:bg-slate-700 my-1.5"></div>
                <button 
                  onClick={() => { setIsMenuOpen(false); onLogout(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all group cursor-pointer"
                >
                  <div className="p-2 bg-rose-50 dark:bg-rose-900/20 rounded-lg group-hover:bg-rose-100 dark:group-hover:bg-rose-800 group-hover:text-rose-600 transition-colors">
                    <LogOut size={14} />
                  </div>
                  Keluar Aplikasi
                </button>
              </motion.div>
            </>
          )}
        </div>
      </div>

      {/* Live Schedule & Agenda Detail Modal */}
      {showLiveDetailModal && activeScheduleItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/70 backdrop-blur-xs" 
            onClick={() => setShowLiveDetailModal(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 p-5 sm:p-6 z-10 max-h-[90vh] overflow-y-auto custom-scrollbar"
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className={`p-2.5 rounded-2xl ${
                  activeScheduleItem.type === 'break' 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                }`}>
                  {activeScheduleItem.type === 'break' ? <Coffee size={20} /> : <BookOpen size={20} />}
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="flex h-2 w-2 relative">
                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                        activeScheduleItem.type === 'break' ? 'bg-amber-400' : 'bg-emerald-400'
                      }`}></span>
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${
                        activeScheduleItem.type === 'break' ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}></span>
                    </span>
                    <span className={`text-[10px] font-black uppercase tracking-wider ${
                      activeScheduleItem.type === 'break' ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'
                    }`}>
                      {activeScheduleItem.type === 'break' ? 'Jam Istirahat' : 'Sedang Berlangsung (Live)'}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-black text-slate-800 dark:text-white leading-snug">
                    {activeScheduleItem.title}
                  </h3>
                </div>
              </div>
              <button
                onClick={() => setShowLiveDetailModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-800 rounded-xl transition-colors"
                title="Tutup"
              >
                <X size={18} />
              </button>
            </div>

            {/* Countdown Banner */}
            <div className={`my-4 p-4 rounded-2xl border flex flex-col items-center justify-center text-center ${
              activeScheduleItem.type === 'break'
                ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800/80 text-amber-900 dark:text-amber-200'
                : 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800/80 text-indigo-900 dark:text-indigo-200'
            }`}>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                Sisa Waktu Sesi
              </span>
              <span className={`text-2xl sm:text-3xl font-black font-mono my-1 tracking-tight ${
                activeScheduleItem.type === 'break'
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-indigo-600 dark:text-indigo-400'
              }`}>
                {activeScheduleItem.remainingText}
              </span>
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                {activeScheduleItem.subTitle} ({activeScheduleItem.timeRange} WIB)
              </span>
            </div>

            {/* Detail Information */}
            <div className="space-y-2.5 text-xs">
              {activeScheduleItem.subject && (
                <div className="flex items-start justify-between py-2 border-b border-slate-100 dark:border-slate-800/60">
                  <span className="font-bold text-slate-400 uppercase text-[10px]">Mata Pelajaran</span>
                  <span className="font-black text-slate-800 dark:text-white text-right max-w-[200px]">{activeScheduleItem.subject}</span>
                </div>
              )}

              {activeScheduleItem.topic && (
                <div className="flex flex-col gap-0.5 py-2 border-b border-slate-100 dark:border-slate-800/60">
                  <span className="font-bold text-slate-400 uppercase text-[10px]">Materi / Topik Pembelajaran</span>
                  <span className="font-extrabold text-slate-800 dark:text-white leading-relaxed">{activeScheduleItem.topic}</span>
                </div>
              )}

              {activeScheduleItem.tpCode && (
                <div className="flex flex-col gap-0.5 py-2 border-b border-slate-100 dark:border-slate-800/60">
                  <span className="font-bold text-slate-400 uppercase text-[10px]">Tujuan Pembelajaran (TP)</span>
                  <span className="font-bold text-indigo-600 dark:text-indigo-400">{activeScheduleItem.tpCode}: <span className="text-slate-700 dark:text-slate-300 font-medium">{activeScheduleItem.tpDescription}</span></span>
                </div>
              )}

              {activeScheduleItem.teacherName && (
                <div className="flex items-start justify-between py-2 border-b border-slate-100 dark:border-slate-800/60">
                  <span className="font-bold text-slate-400 uppercase text-[10px]">Guru Pengampu</span>
                  <span className="font-bold text-slate-800 dark:text-white">{activeScheduleItem.teacherName}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-5 pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row items-center gap-2">
              <button
                onClick={() => {
                  setShowLiveDetailModal(false);
                  onNavigate(View.Agenda);
                }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs transition-all shadow-lg shadow-indigo-500/20 active:scale-95 cursor-pointer"
              >
                <Calendar size={15} />
                <span>Buka Agenda & Presensi Siswa</span>
              </button>
              <button
                onClick={() => setShowLiveDetailModal(false)}
                className="w-full sm:w-auto py-3 px-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-2xl font-bold text-xs transition-all active:scale-95 cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </header>
  );
};

export default Header;
